import { useCallback, useEffect, useState } from 'react';
import { Card, FormField, TextInput, PageHeader } from '@/components/ui';
import { LogoMark } from '@/components/ui/LogoMark';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface TenantLite {
  id: number;
  name: string;
  slug: string;
  branding?: Branding | null;
}

interface Branding {
  brand_name?: string | null;
  primary_color?: string | null;
  logo_url?: string | null;
  domain?: string | null;
}

interface BrandForm {
  brand_name: string;
  primary_color: string;
  logo_url: string;
  domain: string;
}

const DEFAULTS: BrandForm = {
  brand_name: 'Merkaziya',
  primary_color: '#5a3df0',
  logo_url: '',
  domain: '',
};

export function WhiteLabel() {
  const [tenants, setTenants] = useState<TenantLite[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [form, setForm] = useState<BrandForm>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await superApi.tenants({ per_page: 100 });
        const list = (res.data as TenantLite[]) ?? [];
        setTenants(list);
        if (list.length) setTenantId(list[0].id);
      } catch {
        toast('שגיאה בטעינת לקוחות', 'danger');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadBranding = useCallback(async (id: number) => {
    setLoadingTenant(true);
    try {
      const t = (await superApi.tenant(id)) as TenantLite;
      const b = t.branding ?? {};
      setForm({
        brand_name: b.brand_name ?? t.name ?? DEFAULTS.brand_name,
        primary_color: b.primary_color ?? DEFAULTS.primary_color,
        logo_url: b.logo_url ?? DEFAULTS.logo_url,
        domain: b.domain ?? DEFAULTS.domain,
      });
    } catch {
      toast('שגיאה בטעינת מיתוג הלקוח', 'danger');
    } finally {
      setLoadingTenant(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId != null) void loadBranding(tenantId);
  }, [tenantId, loadBranding]);

  async function save() {
    if (tenantId == null) return;
    setSaving(true);
    try {
      await superApi.updateTenant(tenantId, {
        branding: {
          brand_name: form.brand_name || null,
          primary_color: form.primary_color || null,
          logo_url: form.logo_url || null,
          domain: form.domain || null,
        },
      });
      toast('המיתוג נשמר', 'ok');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>טוען…</Card>
      </div>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="White Label" />
      <p style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', margin: 0 }}>
        הגדר מיתוג מותאם לכל לקוח: שם מותג, צבע ראשי, לוגו ודומיין מותאם. ההגדרות נשמרות על הלקוח עצמו.
      </p>

      <Card title="בחר לקוח">
        <select
          className="select"
          value={tenantId ?? ''}
          onChange={(e) => setTenantId(Number(e.target.value))}
          style={{ minWidth: 240 }}
        >
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.slug})
            </option>
          ))}
        </select>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
        <Card title="הגדרות מותג">
          {loadingTenant ? (
            <div style={{ color: 'var(--muted)' }}>טוען…</div>
          ) : (
            <div className="stack">
              <FormField label="שם המותג">
                <TextInput value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} />
              </FormField>
              <FormField label="צבע ראשי" hint="ערך HEX, למשל #5a3df0">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.primary_color) ? form.primary_color : '#5a3df0'}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    style={{ width: 44, height: 38, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'none', cursor: 'pointer' }}
                  />
                  <TextInput value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} dir="ltr" />
                </div>
              </FormField>
              <FormField label="קישור ללוגו" hint="URL לתמונת PNG/SVG">
                <TextInput value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} dir="ltr" placeholder="https://…" />
              </FormField>
              <FormField label="דומיין מותאם">
                <TextInput value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} dir="ltr" placeholder="app.example.com" />
              </FormField>
              <button type="button" className="btn primary" onClick={save} disabled={saving}>
                {saving ? 'שומר…' : 'שמור'}
              </button>
            </div>
          )}
        </Card>

        <Card title="תצוגה מקדימה">
          <div
            style={{
              padding: 24,
              background: 'var(--surface-2)',
              borderRadius: 'var(--r-3)',
              textAlign: 'center',
            }}
          >
            {form.logo_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={form.logo_url}
                alt="logo"
                style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div style={{ color: form.primary_color }}>
                <LogoMark size={48} />
              </div>
            )}
            <h2 style={{ marginTop: 16, color: form.primary_color }}>{form.brand_name || '—'}</h2>
            {form.domain && (
              <p style={{ color: 'var(--muted)' }} dir="ltr">
                {form.domain}
              </p>
            )}
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8, background: form.primary_color, color: '#fff', borderColor: form.primary_color }}
            >
              כפתור לדוגמה
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
